import { useEffect } from 'react';
import { KasPromoCodeFields } from '../../components/KasPromoCodeFields';
import { toKasFormValue, toKasPayload } from '../../components/kasPromoCode';
import {
  Alert,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  TimePicker,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { Chain } from '../catalog/api';
import {
  KIND_OPTIONS,
  SECONDARY_CHANNEL_OPTIONS,
  WEEKDAY_OPTIONS,
  isVoucherKind,
  type Campaign,
  type SecondaryChannel,
  type CampaignConfig,
  type CampaignKind,
  type UpsertCampaignPayload,
  type Weekday,
} from './api';

const WIRE_TIME = 'HH:mm:ss';
const DISPLAY_TIME = 'HH:mm';

interface FormValues {
  name: string;
  description?: string;
  kind: CampaignKind;
  chainId?: string | null;
  /** `Record<chainId, code>` — mảng gửi lên do `toKasPayload` dựng theo phạm vi hiện tại. */
  kasPromoCodes?: Record<string, string>;
  startsAt: Dayjs;
  endsAt?: Dayjs | null;
  pointsBudget?: number | null;
  maxTotalValue?: number | null;
  maxRecipients?: number | null;
  voucherAmount?: number | null;
  voucherValidDays?: number | null;
  minBillAmount?: number | null;
  minGuestCount?: number | null;
  inactiveDays?: number | null;
  pointsMultiplier?: number | null;
  daysOfWeek: Weekday[];
  fromTime?: Dayjs | null;
  toTime?: Dayjs | null;
  sendAtTime?: Dayjs | null;
  secondaryChannel?: SecondaryChannel | null;
  allowStacking: boolean;
}

interface Props {
  campaign: Campaign | null;
  isCreating: boolean;
  chains: Chain[];
  submitting: boolean;
  onSubmit: (payload: UpsertCampaignPayload) => void;
  onClose: () => void;
}

function parseTime(value: string | null | undefined): Dayjs | undefined {
  if (!value) return undefined;
  const parsed = dayjs(value, [WIRE_TIME, DISPLAY_TIME], true);
  return parsed.isValid() ? parsed : undefined;
}

function toWireTime(value: Dayjs | null | undefined): string | null {
  return value ? value.format(WIRE_TIME) : null;
}

function buildConfig(values: FormValues): CampaignConfig {
  const voucher = isVoucherKind(values.kind);
  return {
    voucherAmount: voucher ? (values.voucherAmount ?? null) : null,
    voucherValidDays: voucher ? (values.voucherValidDays ?? null) : null,
    minBillAmount: voucher ? (values.minBillAmount ?? null) : null,
    minGuestCount: voucher ? (values.minGuestCount ?? null) : null,
    inactiveDays: values.kind === 'WinBack' ? (values.inactiveDays ?? null) : null,
    pointsMultiplier: values.kind === 'FlashPoints' ? (values.pointsMultiplier ?? null) : null,
    daysOfWeek: values.kind === 'FlashPoints' ? values.daysOfWeek : null,
    fromTime: values.kind === 'FlashPoints' ? toWireTime(values.fromTime) : null,
    toTime: values.kind === 'FlashPoints' ? toWireTime(values.toTime) : null,
    sendAtTime: voucher ? toWireTime(values.sendAtTime) ?? '08:00:00' : null,
    secondaryChannel: values.secondaryChannel ?? null,
    allowStacking: values.allowStacking,
  };
}

function buildPayload(values: FormValues, chains: Chain[]): UpsertCampaignPayload {
  return {
    kind: values.kind,
    chainId: values.chainId ?? null,
    // LUÔN gửi mảng: bỏ trường đi nghĩa là "không đụng", và mã của chuỗi vừa bị bỏ khỏi
    // phạm vi sẽ nằm lại, hiện ra ở quầy cho thu ngân gõ.
    kasPromoCodes: toKasPayload(values.kasPromoCodes, values.chainId, chains),
    name: values.name.trim(),
    description: values.description?.trim() || null,
    startsAt: values.startsAt.toISOString(),
    endsAt: values.endsAt?.toISOString() ?? null,
    pointsBudget: values.pointsBudget ?? null,
    maxTotalValue: values.maxTotalValue ?? null,
    maxRecipients: values.maxRecipients ?? null,
    config: buildConfig(values),
  };
}

export function CampaignFormModal({
  campaign,
  isCreating,
  chains,
  submitting,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const open = campaign !== null || isCreating;
  const kind = Form.useWatch('kind', form) as CampaignKind | undefined;
  const pointsBudget = Form.useWatch('pointsBudget', form);
  const maxTotalValue = Form.useWatch('maxTotalValue', form);
  const maxRecipients = Form.useWatch('maxRecipients', form);
  const pointsMultiplier = Form.useWatch('pointsMultiplier', form);

  const capsEmpty = pointsBudget == null && maxTotalValue == null && maxRecipients == null;
  const isActive = campaign?.state === 'Active';
  const isEnded = campaign?.state === 'Ended';
  const chainId = Form.useWatch('chainId', form) as string | null | undefined;
  const lockConfig = isActive || isEnded;
  const readOnly = isEnded;

  useEffect(() => {
    if (!open) return;

    if (campaign) {
      const cfg = campaign.config;
      form.setFieldsValue({
        name: campaign.name,
        description: campaign.description ?? undefined,
        kind: campaign.kind,
        chainId: campaign.chainId,
        kasPromoCodes: toKasFormValue(campaign.kasPromoCodes),
        startsAt: dayjs(campaign.startsAt),
        endsAt: campaign.endsAt ? dayjs(campaign.endsAt) : undefined,
        pointsBudget: campaign.pointsBudget,
        maxTotalValue: campaign.maxTotalValue,
        maxRecipients: campaign.maxRecipients,
        voucherAmount: cfg.voucherAmount ?? undefined,
        voucherValidDays: cfg.voucherValidDays ?? undefined,
        minBillAmount: cfg.minBillAmount ?? undefined,
        minGuestCount: cfg.minGuestCount ?? undefined,
        inactiveDays: cfg.inactiveDays ?? undefined,
        pointsMultiplier: cfg.pointsMultiplier ?? undefined,
        daysOfWeek: cfg.daysOfWeek ?? [],
        fromTime: parseTime(cfg.fromTime),
        toTime: parseTime(cfg.toTime),
        sendAtTime: parseTime(cfg.sendAtTime) ?? dayjs('08:00:00', WIRE_TIME),
        // ⚠️ Phải đọc lại, không chỉ ghi. Thiếu dòng này thì mở một chương trình đã chọn SMS ra sửa
        // sẽ thấy ô trống, và bấm Lưu là XÓA MẤT lựa chọn đó — im lặng.
        secondaryChannel: cfg.secondaryChannel ?? undefined,
        allowStacking: cfg.allowStacking,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        kind: 'Welcome',
        startsAt: dayjs(),
        daysOfWeek: [],
        sendAtTime: dayjs('08:00:00', WIRE_TIME),
        allowStacking: false,
        voucherValidDays: 30,
      });
    }
  }, [campaign, open, form]);

  return (
    <Modal
      open={open}
      title={isCreating ? 'Tạo khuyến mãi' : `Sửa ${campaign?.name ?? ''}`}
      okText={readOnly ? 'Đóng' : 'Lưu'}
      cancelText="Hủy"
      width={640}
      confirmLoading={submitting}
      destroyOnHidden
      okButtonProps={readOnly ? { style: { display: 'none' } } : undefined}
      onCancel={onClose}
      onOk={() => {
        if (readOnly) {
          onClose();
          return;
        }
        form
          .validateFields()
          .then((values) => onSubmit(buildPayload(values, chains)))
          .catch(() => {});
      }}
    >
      <Form form={form} layout="vertical" requiredMark={false} disabled={readOnly}>
        {isActive && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Đang chạy — chỉ sửa tên, mô tả, ngày kết thúc và ba trần"
            description="Cấu hình có tác dụng ngay lần quét kế tiếp và không có phiên bản. Muốn đổi loại hay điều kiện thì tạm dừng trước."
          />
        )}

        {isEnded && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Chương trình đã kết thúc, không sửa được."
          />
        )}

        <Form.Item name="name" label="Tên chương trình" rules={[{ required: true, message: 'Chưa nhập tên.' }]}>
          <Input maxLength={200} disabled={readOnly} />
        </Form.Item>

        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} maxLength={1000} disabled={readOnly} />
        </Form.Item>

        <Form.Item name="kind" label="Loại" rules={[{ required: true }]}>
          <Select options={KIND_OPTIONS} disabled={lockConfig || !isCreating} />
        </Form.Item>

        <Form.Item name="chainId" label="Phạm vi chuỗi">
          <Select
            allowClear
            placeholder="Toàn hệ thống"
            options={chains.map((c) => ({ value: c.id, label: c.name }))}
            disabled={lockConfig}
          />
        </Form.Item>
        {/* CỐ Ý không khoá theo `lockConfig`. Quầy đọc mã tại thời điểm quét, không phải lúc phát
            voucher, nên gõ sai một ký tự thì phải vá được ngay giữa lúc chương trình đang chạy —
            khoá lại là buộc người ta kết thúc chương trình để sửa một chuỗi ký tự. Chương trình đã
            kết thúc thì `readOnly` trên thẻ Form vẫn khoá cả khối này. */}
        <Form.Item name="kasPromoCodes" noStyle>
          <KasPromoCodeFields chains={chains} chainId={chainId} />
        </Form.Item>

        <Form.Item name="startsAt" label="Bắt đầu" rules={[{ required: true, message: 'Chưa chọn ngày bắt đầu.' }]}>
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} disabled={lockConfig} />
        </Form.Item>

        <Form.Item name="endsAt" label="Kết thúc">
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} disabled={readOnly} />
        </Form.Item>

        {capsEmpty && !readOnly && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Ba trần đang để trống — không giới hạn"
            description="438 voucher 200.000đ là 87,6 triệu mà không cảnh báo gì. Có khai thì hệ thống kiểm; để trống là chấp nhận rủi ro đó."
          />
        )}

        <Form.Item name="pointsBudget" label="Trần ngân sách điểm">
          <InputNumber min={1} style={{ width: '100%' }} placeholder="Để trống = không giới hạn" disabled={readOnly} />
        </Form.Item>

        <Form.Item name="maxTotalValue" label="Trần tổng giá trị tiền (đồng)">
          <InputNumber min={1} step={10_000} style={{ width: '100%' }} placeholder="Để trống = không giới hạn" disabled={readOnly} />
        </Form.Item>

        <Form.Item name="maxRecipients" label="Trần số khách">
          <InputNumber min={1} style={{ width: '100%' }} placeholder="Để trống = không giới hạn" disabled={readOnly} />
        </Form.Item>

        {kind && isVoucherKind(kind) && (
          <>
            <Form.Item
              name="voucherAmount"
              label="Mệnh giá voucher (đồng)"
              rules={[{ required: !lockConfig, message: 'Chưa nhập mệnh giá voucher.' }]}
            >
              <InputNumber min={1} step={10_000} style={{ width: '100%' }} disabled={lockConfig} />
            </Form.Item>

            <Form.Item
              name="voucherValidDays"
              label={kind === 'Birthday' ? 'Số ngày hiệu lực tối đa của voucher' : 'Số ngày hiệu lực của voucher'}
              rules={[{ required: !lockConfig, message: 'Chưa nhập số ngày hiệu lực.' }]}
              extra={
                kind === 'Birthday'
                  ? 'Voucher sinh nhật hết hạn vào cuối tháng sinh nhật của khách, nhưng luôn còn dùng được ít nhất số ngày đặt ở Thông số vận hành (mặc định 14). Ô này là mức tối đa.'
                  : undefined
              }
            >
              <InputNumber min={1} max={730} style={{ width: '100%' }} disabled={lockConfig} />
            </Form.Item>

            {kind === 'WinBack' && (
              <Form.Item
                name="inactiveDays"
                label="Số ngày không phát sinh hóa đơn"
                rules={[{ required: !lockConfig, message: 'Chưa nhập số ngày vắng.' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} disabled={lockConfig} />
              </Form.Item>
            )}

            <Form.Item name="minBillAmount" label="Hóa đơn tối thiểu (đồng)">
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Để trống = không yêu cầu" disabled={lockConfig} />
            </Form.Item>

            <Form.Item
              name="minGuestCount"
              label="Số khách tối thiểu trên bàn"
              tooltip="Điều khoản hiển thị cho thu ngân. Hệ thống không kiểm được lúc áp voucher vì lúc đó hóa đơn chưa in."
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="Để trống = không ghi" disabled={lockConfig} />
            </Form.Item>

            <Form.Item name="sendAtTime" label="Giờ gửi (giờ Việt Nam)">
              <TimePicker format={DISPLAY_TIME} style={{ width: '100%' }} disabled={lockConfig} />
            </Form.Item>

            {/*
              Gửi thêm qua — KHÔNG phải "chọn kênh". In-app và FCM luôn có, như mọi app thông thường
              (PL chốt 30/08).

              Zalo ZNS MỞ KHOÁ từ `S8-BE-10`: `NotificationDispatchJob` đã có bộ gửi ZNS, và nó hỏi
              hạn mức trước mỗi lô rồi mới gửi. SMS VẪN KHOÁ — chưa có bộ gửi SMS cho thông báo; bộ
              gửi SMS hiện có chỉ phục vụ OTP và đi đường khác hẳn.

              Giữ nguyên nguyên tắc: cho chọn rồi hệ thống im lặng không gửi là núm chết — admin tin
              mình đã bật, khách không nhận gì, và không có lỗi nào để lần ra.
            */}
            <Form.Item
              name="secondaryChannel"
              label="Gửi thêm qua"
              tooltip="Thông báo trong app và FCM luôn được gửi. Ô này là đường gửi thứ hai, cộng thêm. Zalo giới hạn số tin ưu đãi mỗi tháng — hết hạn mức thì những khách còn lại vẫn nhận thông báo trong app như bình thường."
            >
              <Select
                allowClear
                placeholder="Chỉ trong app"
                disabled={lockConfig}
                options={SECONDARY_CHANNEL_OPTIONS.map((o) => ({
                  value: o.value,
                  disabled: o.disabled,
                  label: o.disabled ? `${o.label} — ${o.reason}` : o.label,
                }))}
              />
            </Form.Item>
          </>
        )}

        {kind === 'FlashPoints' && (
          <>
            <Form.Item
              name="pointsMultiplier"
              label="Hệ số nhân điểm"
              rules={[{ required: !lockConfig, message: 'Chưa nhập hệ số nhân điểm.' }]}
              extra={
                pointsMultiplier != null && pointsMultiplier < 1
                  ? `Hệ số ${pointsMultiplier} là giảm điểm. Phase 1 không có mẫu này — kiểm lại trước khi bật.`
                  : undefined
              }
            >
              <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} disabled={lockConfig} />
            </Form.Item>

            <Form.Item name="daysOfWeek" label="Thứ trong tuần">
              <Select
                mode="multiple"
                allowClear
                options={WEEKDAY_OPTIONS}
                placeholder="Để trống = mọi ngày"
                disabled={lockConfig}
              />
            </Form.Item>

            <Form.Item name="fromTime" label="Từ giờ (giờ Việt Nam)">
              <TimePicker format={DISPLAY_TIME} style={{ width: '100%' }} disabled={lockConfig} />
            </Form.Item>

            <Form.Item
              name="toTime"
              label="Đến giờ"
              dependencies={['fromTime']}
              extra="Muốn chạy xuyên đêm thì tách hai khung trong ngày: một khung tới 23:59 và một khung từ 00:00."
              rules={[
                {
                  validator: (_, value: Dayjs | undefined) => {
                    const from = form.getFieldValue('fromTime') as Dayjs | undefined;
                    if (!value || !from) return Promise.resolve();
                    if (!value.isAfter(from)) {
                      return Promise.reject(
                        new Error('Giờ kết thúc phải sau giờ bắt đầu trong cùng ngày.'),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TimePicker format={DISPLAY_TIME} style={{ width: '100%' }} disabled={lockConfig} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="allowStacking"
          label="Cho phép dùng chung với ưu đãi khác trên một hóa đơn"
          valuePropName="checked"
          tooltip="Mặc định tắt: một hóa đơn một ưu đãi. Chiết khấu hạng thẻ luôn đi kèm, không bị ô này chặn."
        >
          <Switch disabled={lockConfig} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
